import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { AgentTypes, EventSources, EventTypes } from '../constants/event';
import { readEnvVariable } from '../utils';
import {
  convertHypothesisItems,
  getCartItemProperties,
  getValidCartItems,
} from '../utils/cart';
import logger from '../utils/logger';
import { sendOrderMetrics, setPosCartItems } from './cartSlice';
import { selectMenuVersion } from './menuSlice';
import {
  CheckTransmissionMessage,
  IMessageReceived,
  InfoTransmissionMessage,
  IStatusTransmissionMessage,
  messagingActions,
  sendHITLSessionEnd,
  sendOrder as sendOrderMessage,
} from './messagingSlice';
import { LoyaltyResponseStatus, OrderStatus } from './orderSlice.constants';
import { ICartError } from '../types/brandMenu';
import {
  IOrderTransmissionMessage,
  ISendItemProperties,
  ISendOrderData,
} from './orderSlice.props';
import {
  buildMenuModuleItems,
  buildOrderMetadata,
  extractOrderId,
  initialOrderState,
  populateMUOrderItems,
  processOrderStatusAndCheckResponses,
  setLastOrderItemsFailed,
} from './orderSlice.utils';
import { selectRestaurant, selectStage } from './restaurantSlice';
import { getMenuUnificationFeatureFlag } from '../utils/session-storage';
import { sendMenuUnificationOrder } from '../utils/network';
import { fillMUAutoCombinedItems, isValidResponse } from './orderSlice.bm';
import { ICartLoyalty } from './cartSlice.props';
import { setFinalOrderSubmitted } from '../redux/features/sessionBoundary/sessionBoundary.slice';

export const sendOrder = createAsyncThunk(
  'order/sendOrder',
  async (
    {
      isFinal,
      bypassAIAutoMode,
      menuUnificationOrderFailureCallback,
      menuUnificationOrderSuccessCallback,
    }: {
      isFinal?: boolean;
      /** Submit even while the AI holds auto mode. Set ONLY by the
       * staff-intervention path (HITL-1551), which must cover every escalated
       * car — see the note on the guard below. */
      bypassAIAutoMode?: boolean;
      menuUnificationOrderFailureCallback?: Function;
      menuUnificationOrderSuccessCallback?: Function;
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    const {
      config: {
        ITEM_BY_ITEM: isItemByItemEnabled,
        MU_MENU_API,
        MU_MENU_API_TOKEN,
        FEATURE_FLAG_AUTO_COMBO,
        FEATURE_FLAG_BM_AUTO_COMBO,
        FEATURE_FLAG_FINAL_ORDER_VALIDATION,
        FEATURE_FLAG_LOYALTY_SUPPORT,
        FEATURE_FLAG_USE_MENU_MODULE_FOR_ORDER_SUBMISSION,
      },
      menu: { topLevelMenuItems },
      cart: {
        cartItemsQuantity,
        loyaltyCouponItem,
        coupons,
        cartItems,
        posCartItems,
        hypothesisCartItems,
        loyalties: cartLoyalties,
      },
      restaurant,
      messages: { startFrame, currentSessionId },
      ai: { isAIAutoMode },
      sessionBoundary: { sessionId: sbSessionId, endedSessionId },
      taskRouter: { taskAssignedSessionId, taskAssignedLaneId },
      autoCombo: { autoCombinedCartItemsQuantity, autoCombinedCartItems },
      order: { menuModuleRequestSeqId, customerName, isAutofillFinalInFlight },
    } = getState();

    const isUnifiedMenu = getMenuUnificationFeatureFlag();

    // Do not send order if in AI auto mode -- the AI owns the cart while it is
    // driving.
    //
    // HITL-1551 opts out explicitly rather than relying on `isAIAutoMode` being
    // false by the time a staff intervention submits. It usually IS false, but
    // not for the reason it looks: `sendAgentInterception` clears the flag
    // (ai.slice.ts) for EVERY interception type, and `TTS_SENT` is one of them
    // (menuSlice.ts, useTTSMessage, usePOSCart, useReadback...). So ordinary
    // AI-driven ordering clears it with no agent involvement at all, and the
    // flag does not mean what its own comment says ("whether agent interception
    // happens or not"). Depending on that would make the intervention submit
    // work by accident, and silently stop covering the cars that never emitted
    // a TTS.
    if (isAIAutoMode && !bypassAIAutoMode) return;

    // HITL-1551: the staff-intervention autofill is mid-flight. Its fill flips
    // items from invalid to valid, so `selectValidCartItems` changes and
    // OrderReview's cart-change effect (OrderReview.tsx:105) dispatches an
    // interim send — which would land on the POS immediately BEHIND the final
    // order, and would also bump `menuModuleRequestSeqId` and so gate the
    // final's own success/failure callbacks out as stale. An interim order is
    // never wanted once a final is on its way.
    if (!isFinal && isAutofillFinalInFlight) {
      logger.info({
        message:
          'Suppressing interim order: the staff-intervention final submit is in flight',
        sessionId: getState().sessionBoundary?.sessionId,
      });
      return;
    }

    const autoCombined =
      (FEATURE_FLAG_AUTO_COMBO ||
        (isUnifiedMenu && FEATURE_FLAG_BM_AUTO_COMBO)) &&
      Object.values(autoCombinedCartItems).length;

    const itemsQuantity = autoCombined
      ? autoCombinedCartItemsQuantity
      : cartItemsQuantity;
    const validHITLCartItems = autoCombined
      ? getValidCartItems(autoCombinedCartItems)
      : getValidCartItems(cartItems);
    const validCartItems = Object.values({
      ...validHITLCartItems,
      ...hypothesisCartItems,
    });

    // Do not send order for if no valid cart item or order loyalty item
    if (
      validCartItems.length === 0 &&
      (FEATURE_FLAG_LOYALTY_SUPPORT ? !cartLoyalties?.length : true)
    )
      return;

    const validCartItemIds = validCartItems.reduce(
      (acc: number[], { cartItemId }) => {
        if (cartItemId) acc.push(cartItemId);
        return acc;
      },
      []
    );

    dispatch(orderActions.startSendOrder(validCartItemIds));

    const orderId = extractOrderId(getState()); // The session id inside the data in order event to support multiple order per session.
    dispatch(orderActions.setCurrentSession(orderId)); // The currentSessionId should in sync with sessionId which is sent in order event

    let restaurantCode =
      restaurant.selectedRestaurantDetails.restaurantCode || undefined;

    if (!restaurantCode) {
      restaurantCode = startFrame?.data.restaurant_code;
      if (!restaurantCode) {
        throw rejectWithValue('No Current restaurantCode selected!');
      }
    }

    const requestId = uuidv4();
    const orderSentAt = new Date().toISOString();

    // Build shared order metadata (loyalties, customer_info, coupons, couponno)
    // This is used by both menu module send order flow and HITL send order flow
    // Note: Side-effect dispatches (startSendLoyalty, startSendCoupon, sendInfo) are NOT called here
    // They are called only after confirming the order will actually be sent
    const {
      metadata: orderMetadata,
      couponIds,
      dispatchData,
    } = buildOrderMetadata({
      FEATURE_FLAG_LOYALTY_SUPPORT,
      cartLoyalties,
      customerName,
      sbSessionId,
      loyaltyCouponItem,
      coupons,
    });

    // Helper function to dispatch side effects (only call after confirming order will be sent)
    // Note: dispatchData.cartLoyalties is already filtered by FEATURE_FLAG_LOYALTY_SUPPORT in buildOrderMetadata
    const dispatchOrderSideEffects = () => {
      if (dispatchData.cartLoyalties.length) {
        dispatch(orderActions.startSendLoyalty(dispatchData.cartLoyalties));
      }
      if (dispatchData.couponIds.length) {
        dispatch(orderActions.startSendCoupon(dispatchData.couponIds));
        if (isFinal) {
          // Send coupon added metric in the final order
          const couponCodes = dispatchData.couponObjList.reduce(
            (acc: string[], couponObj) => {
              acc.push(couponObj.code);
              return acc;
            },
            []
          );
          const couponMetricPayload: Partial<InfoTransmissionMessage> = {
            data: {
              message: 'Coupon added',
              type: 'METRIC',
              code: 'COUPON_ADDED',
              metadata: { coupons: couponCodes },
            },
          };
          dispatch(messagingActions.sendInfo(couponMetricPayload as any));
        }
      }
    };

    // Only the most recent menu-module request may apply success/failure side
    // effects (error highlights, resets). Responses can resolve out of order;
    // a callback from a superseded send would paint state derived from a cart
    // that no longer exists. Same staleness rule as the `data` check below,
    // extended to the callbacks.
    const gateOnLatestRequest =
      (seqId: number, callback?: Function) =>
      (...args: unknown[]) => {
        if (getState().order.menuModuleRequestSeqId === seqId) {
          callback?.(...args);
        }
      };

    // Build order data first (needed for both old and new flows)
    // Note: items array will be populated differently based on the flow
    let items: ISendItemProperties[] = [];

    // If menu module send order feature is enabled and we're in unified menu mode,
    // send order data to menu module and let it send the order event
    if (
      FEATURE_FLAG_USE_MENU_MODULE_FOR_ORDER_SUBMISSION &&
      isUnifiedMenu &&
      (Object.values(validHITLCartItems).length ||
        (FEATURE_FLAG_LOYALTY_SUPPORT && cartLoyalties.length))
    ) {
      // New flow: Menu module will send the order event
      const { menuModuleItems, menuModuleItemsCartItemIds } =
        buildMenuModuleItems(validHITLCartItems, itemsQuantity);

      // Group order metadata for menu module
      const orderMetadataForMenuModule = {
        cartLoyalties,
        coupons: orderMetadata.coupons,
        customerInfo: orderMetadata.customer_info,
        couponno: orderMetadata.couponno,
      };

      const outSessionId =
        taskAssignedSessionId ||
        sbSessionId ||
        endedSessionId ||
        currentSessionId; // Use the same logic as middleware.ts to get the root level session id for sending events

      // Increase menu module seq id so late responses from superseded sends
      // are recognized as stale in the callback gate.
      const nextMenuModuleRequestSeqId = menuModuleRequestSeqId + 1;
      dispatch(setMenuModuleRequestSeqId(nextMenuModuleRequestSeqId));

      // Call menu unification API with order data for menu module to send order event
      const orderSentAt = new Date().toISOString();
      let data = await sendMenuUnificationOrder(
        MU_MENU_API,
        MU_MENU_API_TOKEN,
        FEATURE_FLAG_BM_AUTO_COMBO,
        FEATURE_FLAG_LOYALTY_SUPPORT,
        restaurantCode,
        menuModuleItems,
        menuModuleItemsCartItemIds,
        validCartItemIds,
        outSessionId,
        gateOnLatestRequest(
          nextMenuModuleRequestSeqId,
          menuUnificationOrderFailureCallback
        ),
        gateOnLatestRequest(
          nextMenuModuleRequestSeqId,
          menuUnificationOrderSuccessCallback
        ),
        {
          useMenuModuleForOrderSubmission:
            FEATURE_FLAG_USE_MENU_MODULE_FOR_ORDER_SUBMISSION,
          isFinal,
          requestId,
          orderId,
          laneId: taskAssignedLaneId,
          orderMetadata: orderMetadataForMenuModule,
        }
      );

      if (data) {
        // Order will be sent by menu module - dispatch side effects now
        dispatchOrderSideEffects();

        // Menu module will send the order event, so we just need to track the request
        dispatch(
          populateOrderRequests({
            requestId: requestId,
            loyalties: orderMetadata.loyalties || [],
            cartItemIds: validCartItemIds,
            isFinal,
            couponIds,
            orderId,
            orderSentAt,
            origin: 'AGENT',
          })
        );
        dispatch(sendOrderMetrics());
        if (isFinal) {
          if (FEATURE_FLAG_FINAL_ORDER_VALIDATION) {
            dispatch(orderActions.setFinalOrderStatus(OrderStatus.pending));
          } else {
            dispatch(setFinalOrderSubmitted(true));
            dispatch(orderActions.finishOrderResetValues());
          }
        }
        return; // Menu module will send the order event, so we return early
      } else {
        // Menu unification API failed (e.g. HTTP 400 unavailable_items). For a
        // FINAL submit the failure callback has already populated the
        // menu-module error UI (banner + failing-node highlights); the session
        // intentionally stays open (no finalOrderSubmitted, no finalOrderStatus
        // change — that would trigger FinalOrderBackdrop's failed handler,
        // which auto-requests staff and force-advances the session) so the
        // agent can fix or remove the failing item and click Finish Order again.
        if (isFinal) {
          logger.error({
            restaurantCode,
            message: 'Final order submission failed at menu unification API',
            sessionId: sbSessionId,
          });
        }
        return;
      }
    }

    // Original flow: Build items and send order event from HITL
    if (
      isUnifiedMenu &&
      (Object.values(validHITLCartItems).length ||
        (FEATURE_FLAG_LOYALTY_SUPPORT && cartLoyalties.length))
    ) {
      if (isFinal && posCartItems.length) {
        // Final order request should have the same cart items with the previous request but with final flag true. So no need to call the menu module API again.
        items = posCartItems;
      } else {
        const { menuModuleItems, menuModuleItemsCartItemIds } =
          buildMenuModuleItems(validHITLCartItems, itemsQuantity);

        // Increase menu module seq id
        const nextMenuModuleRequestSeqId = menuModuleRequestSeqId + 1;
        dispatch(setMenuModuleRequestSeqId(nextMenuModuleRequestSeqId));

        // Call menu unification API to get POS-specific item data
        let data = await sendMenuUnificationOrder(
          MU_MENU_API,
          MU_MENU_API_TOKEN,
          FEATURE_FLAG_BM_AUTO_COMBO,
          FEATURE_FLAG_LOYALTY_SUPPORT,
          restaurantCode,
          menuModuleItems,
          menuModuleItemsCartItemIds,
          validCartItemIds,
          sbSessionId || orderId,
          gateOnLatestRequest(
            nextMenuModuleRequestSeqId,
            menuUnificationOrderFailureCallback
          ),
          gateOnLatestRequest(
            nextMenuModuleRequestSeqId,
            menuUnificationOrderSuccessCallback
          ),
          {
            useMenuModuleForOrderSubmission:
              FEATURE_FLAG_USE_MENU_MODULE_FOR_ORDER_SUBMISSION,
            isFinal,
            orderMetadata: { cartLoyalties },
          }
        );
        let posData, bmData;

        // Check if the order was successfully sent
        if (data) {
          const latestMenuModuleRequestSeqId =
            getState().order.menuModuleRequestSeqId;
          if (nextMenuModuleRequestSeqId === latestMenuModuleRequestSeqId) {
            logger.debug({
              message: `Menu module order response: ${sbSessionId || orderId}`,
              posItemCount: data?.pos_order_items?.length,
              bmItemCount: data?.brand_menu_items?.length,
            });
            if (FEATURE_FLAG_BM_AUTO_COMBO || FEATURE_FLAG_LOYALTY_SUPPORT) {
              const { pos_order_items, brand_menu_items } = data || {};
              posData = pos_order_items;
              bmData = brand_menu_items;
            } else {
              posData = data;
            }
            if (
              FEATURE_FLAG_BM_AUTO_COMBO &&
              isValidResponse(bmData, topLevelMenuItems)
            ) {
              fillMUAutoCombinedItems({
                dispatch,
                getState,
                bmData,
                menuModuleItems,
                menuModuleItemsCartItemIds,
              });
              // Brand menu auto-combination has happened
              logger.debug({
                restaurantCode,
                message: 'Brand Menu: Auto combination of items happened',
                sessionId: sbSessionId || orderId,
              });
            } else if (FEATURE_FLAG_BM_AUTO_COMBO) {
              logger.debug({
                restaurantCode,
                sessionId: sbSessionId || orderId,
                message: `Brand Menu: received unavailable items in response`,
              });
            }
            items = populateMUOrderItems(posData);
            dispatch(setPosCartItems(items));
          } else {
            // This is an outdated response from menu module, ignore it
            logger.debug({
              message: `Ignoring outdated menu module response for seqId: ${
                nextMenuModuleRequestSeqId - 1
              }`,
            });
            return; // Early return for outdated responses
          }
        } else {
          // Early return if menu unification API returns falsy data
          // This prevents sending orders with invalid/empty item data.
          // For a FINAL submit the failure surfaces via the error UI (same
          // rationale as the menu-module-submission branch above) so the agent
          // can fix the cart and re-click Finish — no silent swallow, no
          // auto-cancel, no finalOrderStatus change.
          if (isFinal) {
            logger.error({
              restaurantCode,
              message: 'Final order submission failed at menu unification API',
              sessionId: sbSessionId,
            });
          }
          return;
        }
      }
    } else {
      items = Object.values(validHITLCartItems).map((item) => {
        let groupId: string[] = [];

        return getCartItemProperties(
          item,
          groupId,
          [],
          restaurantCode || '',
          itemsQuantity
        );
      });
    }

    // This is tighter than it was, but this really should be atomic...
    dispatch(orderSlice.actions.incSeqId({}));
    const {
      order: { seqId: orderSeqId },
      menu,
    } = getState();
    const seqId = orderSeqId;

    const orderData: ISendOrderData = {
      check_id: '-1', // TODO Do we send the same transaction_id here???
      store_id: restaurantCode,
      final: isFinal ? true : false,
      items:
        Object.values(hypothesisCartItems).length > 0
          ? [
              ...items,
              ...convertHypothesisItems(Object.values(hypothesisCartItems)),
            ]
          : items,
      source: EventSources.prestoVoice,
      request_id: requestId,
      session_id: orderId,
      seq_id: seqId,
      menu_version: menu?.selectedMenuVersion || '',
      ...orderMetadata, // Apply shared metadata (loyalties, customer_info, coupons, couponno)
    };

    const payload: Partial<IOrderTransmissionMessage> = {
      data: {
        ...orderData,
        environment: readEnvVariable('DEPLOY_ENV'),
      },
    };

    // Validate that order has items before sending
    // Log error if attempting to send order with empty items
    if (Object.values(orderData.items).length === 0) {
      logger.warn({
        restaurantCode,
        message: 'Sending order with empty items',
        sessionId: sbSessionId,
      });
    }

    // Only send order message if:
    // 1. Order has items (length > 0)
    // 2. AND either it's a final order OR item-by-item mode is enabled
    // This prevents sending empty orders and ensures proper order flow
    if (
      (Object.values(orderData.items).length > 0 ||
        orderData.loyalties?.length) &&
      (isFinal || isItemByItemEnabled)
    ) {
      // Order will be sent - dispatch side effects now
      dispatchOrderSideEffects();

      logger.debug({
        restaurantCode,
        message: 'Sending order with this data',
        sessionId: sbSessionId,
        isFinal,
      });
      dispatch(sendOrderMessage(payload as any));
      dispatch(
        populateOrderRequests({
          requestId: orderData.request_id,
          loyalties: orderData.loyalties || [],
          cartItemIds: validCartItemIds,
          isFinal,
          couponIds,
          orderId,
          orderSentAt,
          origin: 'AGENT',
        })
      );
      dispatch(sendOrderMetrics());
      if (isFinal) {
        if (FEATURE_FLAG_FINAL_ORDER_VALIDATION) {
          dispatch(orderActions.setFinalOrderStatus(OrderStatus.pending));
        } else {
          dispatch(setFinalOrderSubmitted(true));
          dispatch(orderActions.finishOrderResetValues());
        }
      }
    }
  }
);

const orderSlice = createSlice({
  name: 'order',
  initialState: initialOrderState,
  reducers: {
    startSendOrder: (state, action: PayloadAction<number[]>) => {
      for (let cartItemId of action.payload) {
        if (
          !state.currentTransactionItems[cartItemId] ||
          state.currentTransactionItems[cartItemId] === OrderStatus.failed
        ) {
          state.currentTransactionItems[cartItemId] = OrderStatus.sending;
        }
      }
    },
    populateOrderRequests: (
      state,
      {
        payload: {
          requestId,
          cartItemIds,
          isFinal,
          couponIds,
          orderId,
          loyalties,
          orderSentAt,
          origin,
        },
      }
    ) => {
      // The same request id can be recorded twice: its order event arriving on
      // the websocket (messageReceived tracks non-HITL orders, HITL-1505) and
      // this action from the send path / useExternalOrderHandler — which also
      // re-fires on every cart change while the AI drives. Merge instead of
      // clobber: keep responses the POS already gave (wiping checkResponse
      // would resurrect the Retry banner for a confirmed order) and the first
      // orderSentAt (the overdue clock must not restart), and never duplicate
      // the requestsOrder entry (only the LAST entry's check is processed).
      const existing = state.requestsById[requestId];
      state.requestsById[requestId] = {
        id: requestId,
        // An explicit origin corrects the reducer's wire-time guess (a
        // menu-module order event for a HITL send is recorded as 'AI' before
        // sendOrder's populate names it 'AGENT').
        origin: origin ?? existing?.origin,
        cartItemIds,
        status: existing?.status ?? '',
        transactionId: existing?.transactionId ?? '',
        orderStatusResponse: existing?.orderStatusResponse ?? null,
        checkResponse: existing?.checkResponse ?? null,
        isFinal: isFinal ? true : false,
        couponIds,
        orderId,
        loyalties,
        orderSentAt: existing?.orderSentAt ?? orderSentAt,
      };
      if (!state.requestsOrder.includes(requestId)) {
        state.requestsOrder.push(requestId);
      }
    },
    resetSession: () => {
      logger.info('Reset order states');
      return initialOrderState;
    },
    finishOrderResetValues: (state) => {
      state.currentSessionId = uuidv4(); //Whenever 'Cancel order' or 'Finish order' button is clicked, the next order which goes from HITL will have a new inner session id
      state.currentTransactionItems = {};
      state.seqId = 0;
      state.total = '';
      state.subtotal = '';
      state.tax = '';
      state.completeClickCount = {};
      state.isPosmonDisconnected = false;
      state.isPosmonReconnected = false;
      state.currentTransactionCoupons = {};
      state.currentTransactionLoyalties = {};
      // Keep the final order's request through the reset (HITL-1527): the
      // Order Status Banner tracks its pipeline, and its check event can only
      // be processed while the request is still the latest entry. Without
      // this, the entry is wiped in the same dispatch chain that sends the
      // final order, so no surface can ever show a HITL-clicked final's
      // progress (HITL-1513 scoped itself to AI finals for exactly this
      // reason). Cancel Order still wipes fully — its latest request is not
      // final. The preserved final keeps its orderEventAtById entry too, so
      // ORDER_PROCESSING_METRIC keeps its server anchor for finals.
      const lastRequestId = state.requestsOrder[state.requestsOrder.length - 1];
      const lastRequest = lastRequestId
        ? state.requestsById[lastRequestId]
        : undefined;
      if (lastRequest?.isFinal) {
        state.requestsOrder = [lastRequestId];
        state.requestsById = { [lastRequestId]: lastRequest };
        state.orderEventAtById = state.orderEventAtById?.[lastRequestId]
          ? { [lastRequestId]: state.orderEventAtById[lastRequestId] }
          : {};
      } else {
        state.requestsOrder = [];
        state.requestsById = {};
        state.orderEventAtById = {};
      }
      state.finalOrderStatus = undefined;
      state.orderError = '';
      state.menuModuleCartErrors = [];
      state.errorCartItemId = undefined;
      state.checkEventItems = [];
      state.menuModuleRequestSeqId = 0;
      // Don't reset customer name when finish order or cancel order is clicked
      // Customer name will only be reset when session boundary ends or restaurant code changes
    },
    setCurrentSession: (state, action: PayloadAction<string>) => {
      state.currentSessionId = action.payload;
    },
    incSeqId: (state, action: any) => {
      state.seqId += 1;
    },
    setOrderError: (state, action: PayloadAction<string>) => {
      state.orderError = action.payload;
    },
    setMenuModuleCartErrors: (state, action: PayloadAction<ICartError[]>) => {
      state.menuModuleCartErrors = action.payload;
    },
    // Clear all menu-module error UI state (summary banner, per-item red
    // message, failed item flags). Dispatched on a successful order translation
    // so a prior failure's highlights don't linger.
    resetMenuModuleErrorState: (state) => {
      state.orderError = '';
      state.errorCartItemId = undefined;
      state.menuModuleCartErrors = [];
      Object.keys(state.currentTransactionItems).forEach((cartItemId) => {
        if (state.currentTransactionItems[cartItemId] === OrderStatus.failed) {
          delete state.currentTransactionItems[cartItemId];
        }
      });
    },
    increaseCompleteClickCount: (state) => {
      if (
        state.currentTransactionId &&
        state.currentTransactionId in state.completeClickCount
      ) {
        state.completeClickCount[state.currentTransactionId] += 1;
      } else if (state.currentTransactionId) {
        // update count whenever transaction id updates
        state.completeClickCount = {};
        state.completeClickCount[state.currentTransactionId] = 1;
      }
    },
    resetCurrentTransactionId: (state) => {
      state.currentTransactionId = null;
    },
    startSendCoupon: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((couponId) => {
        if (!state.currentTransactionCoupons[couponId]) {
          state.currentTransactionCoupons[couponId] = OrderStatus.sending;
        }
      });
    },
    startSendLoyalty: (state, action: PayloadAction<ICartLoyalty[]>) => {
      const currentTransactionLoyalties: {
        [loyaltyCode: string]: LoyaltyResponseStatus;
      } = {};
      action.payload.forEach(({ code }) => {
        let status = LoyaltyResponseStatus.sending;
        if (
          state.currentTransactionLoyalties[code] ===
          LoyaltyResponseStatus.valid
        ) {
          status = LoyaltyResponseStatus.valid;
        }
        currentTransactionLoyalties[code] = status;
      });

      state.currentTransactionLoyalties = currentTransactionLoyalties;
    },
    setCurrentTransactionItemStatus: (
      state,
      action: PayloadAction<{ cartItemId: string; orderStatus: OrderStatus }>
    ) => {
      const { cartItemId, orderStatus } = action.payload;
      state.currentTransactionItems[cartItemId] = orderStatus;
    },
    setErrorCartItemId: (state, action: PayloadAction<number>) => {
      state.errorCartItemId = action.payload;
    },
    setCustomerName: (state, action: PayloadAction<string>) => {
      state.customerName = action.payload;
    },
    // HITL-1551. See the field's note in orderSlice.props.ts.
    setIsAutofillFinalInFlight: (state, action: PayloadAction<boolean>) => {
      state.isAutofillFinalInFlight = action.payload;
    },
    setFinalOrderStatus: (
      state,
      action: PayloadAction<OrderStatus | undefined>
    ) => {
      state.finalOrderStatus = action.payload;
    },
    setMenuModuleRequestSeqId: (state, action: PayloadAction<number>) => {
      state.menuModuleRequestSeqId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      messagingActions.messageReceived,
      (state, action: PayloadAction<IMessageReceived>) => {
        const {
          messages,
          isSessionBufferedMessages,
          FEATURE_FLAG_FINAL_ORDER_VALIDATION,
          FEATURE_FLAG_LOYALTY_SUPPORT,
          FEATURE_FLAG_MASK_ORDER_PENDING_ERROR,
        } = action.payload;
        messages.forEach((message) => {
          if (message.event === EventTypes.check) {
            const { data } = message as CheckTransmissionMessage;
            const { request_id: requestId } = data || {};

            if (
              state.requestsOrder.length &&
              state.requestsOrder[state.requestsOrder.length - 1] === requestId
            ) {
              // Always process the check event with the latest request id
              state.requestsById[requestId].checkResponse = message;
              processOrderStatusAndCheckResponses(
                state,
                requestId,
                !!FEATURE_FLAG_FINAL_ORDER_VALIDATION,
                !!FEATURE_FLAG_LOYALTY_SUPPORT,
                !!FEATURE_FLAG_MASK_ORDER_PENDING_ERROR
              );

              // Save check event items to state
              const { check } = data || {};
              if (check && check.items) {
                state.checkEventItems = check.items;
              }
            }
          } else if (message.event === EventTypes.orderStatus) {
            const { data: { request_id: requestId = '' } = {} } =
              message as IStatusTransmissionMessage;

            if (state.requestsById[requestId]) {
              state.requestsById[requestId].orderStatusResponse = message;
              processOrderStatusAndCheckResponses(
                state,
                requestId,
                !!FEATURE_FLAG_FINAL_ORDER_VALIDATION,
                !!FEATURE_FLAG_LOYALTY_SUPPORT,
                !!FEATURE_FLAG_MASK_ORDER_PENDING_ERROR
              );
            }
          } else if (message.event === EventTypes.connect) {
            // trigger send order
            state.isPosmonReconnected = true;

            state.isPosmonDisconnected = false;
          } else if (message.event === EventTypes.disconnect) {
            // display error message
            const posmonDisconnectMessage = 'POSMON is not connected';
            setLastOrderItemsFailed(state, posmonDisconnectMessage);

            // trigger health status check
            state.isPosmonDisconnected = true;

            state.isPosmonReconnected = false;
          } else if (message.event === EventTypes.order) {
            const { data, timestamp } = message as IOrderTransmissionMessage;
            const { seq_id: innerSequenceId = 0, request_id: requestId } =
              data || {};

            //Check event received for AI order: LLM or PNLU - next sequence to be greater than the AI order one
            state.seqId = innerSequenceId;

            // HITL-1513: keep the event-bus timestamp so
            // ORDER_PROCESSING_METRIC can measure server-to-server instead of
            // subtracting a browser clock from a server one. Written to the
            // side map, never onto requestsById[requestId]: this event
            // routinely lands BEFORE the entry exists (menu-module populate
            // resolves later; the AI path's useEffect runs after this
            // reducer). Keyed writes are unconditional, so arrival order
            // stops mattering.
            if (requestId && timestamp) {
              // Tolerate a slice preloaded without this key (test fixtures
              // build partial order state).
              if (!state.orderEventAtById) state.orderEventAtById = {};
              state.orderEventAtById[requestId] = timestamp;
            }

            // Track orders another agent submitted (the AI, directly or via
            // the menu module) so their check events are processed and the
            // retry machinery (useOrderCheckRetry) covers them — otherwise an
            // agent inheriting a stuck AI order gets a grey progress bar and
            // no Retry, and jiggles the cart to force a resend (HITL-1505).
            // Live events only: the session-buffer replay is recorded by
            // useExternalOrderHandler at assignment, and buffered CHECK events
            // are never replayed — recording a replayed order here would
            // raise a false Retry banner for an order the POS already
            // confirmed before takeover. orderSentAt is client receipt time,
            // like every other request, so the overdue clock never mixes
            // client and server clocks. The HITL agent_type guard is defensive
            // only — Kafka does not route a producer's own events back, so
            // HITL never receives its own order events; the requestsById check
            // is what actually dedupes menu-module events for HITL-initiated
            // orders.
            if (
              !isSessionBufferedMessages &&
              requestId &&
              !state.requestsById[requestId] &&
              message.agent_type !== AgentTypes.HITL
            ) {
              state.requestsById[requestId] = {
                id: requestId,
                origin: 'AI',
                cartItemIds: [],
                status: '',
                transactionId: '',
                orderStatusResponse: null,
                checkResponse: null,
                isFinal: !!data?.final,
                couponIds: [],
                orderId: '',
                loyalties: [],
                orderSentAt: new Date().toISOString(),
              };
              state.requestsOrder.push(requestId);
            }
          }
        });
      }
    );
    builder.addMatcher(
      isAnyOf(
        selectRestaurant.fulfilled,
        selectStage.fulfilled,
        selectMenuVersion.fulfilled,
        sendHITLSessionEnd
      ),
      () => {
        return initialOrderState;
      }
    );
  },
});

export const orderActions = orderSlice.actions;
export const {
  startSendOrder,
  setCurrentSession,
  populateOrderRequests,
  setOrderError,
  setMenuModuleCartErrors,
  resetMenuModuleErrorState,
  finishOrderResetValues,
  resetCurrentTransactionId,
  startSendCoupon,
  startSendLoyalty,
  setCurrentTransactionItemStatus,
  setErrorCartItemId,
  setCustomerName,
  setFinalOrderStatus,
  setIsAutofillFinalInFlight,
  setMenuModuleRequestSeqId,
} = orderSlice.actions;

export default orderSlice.reducer;
